<template>
  <div class="d-flex flex-column">
    <DxDataGrid
      v-bind="gridProperties"
      :data-source="gridSource"
      ref="grid"
      class="dx-card wide-card grid-operaciones"
      @selection-changed="onSelectionChanged"
      :hover-state-enabled="true"
    >
      <DxToolbar>
        <DxItem location="after" template="exportButtonTemplate" />
        <DxItem v-bind="getResetFiltersGridBtn(() => grid)" />
        <DxItem v-bind="getResetStateGridBtn(() => grid)" />
        <DxItem v-bind="getColumnChooserBtn()" />
      </DxToolbar>

      <DxSelection mode="single" />
      <DxColumn data-field="CodigoSecuencial" :caption="$t('operaciones.grid.id')" format="0000000000" :show-in-column-chooser="false"></DxColumn>
      <DxColumn data-field="FechaAlta" :caption="$t('operaciones.grid.fecha_de_venta')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="NombreComercial" :caption="$t('operaciones.grid.comercial')" />
      <DxColumn data-field="NombreEmpresaAgente" :caption="$t('operaciones.grid.empresa_agente')"></DxColumn>
      <DxColumn data-field="TipoEmpresaAgente" :caption="$t('operaciones.grid.tipo_empresa_agente')" cell-template="tipoEmpresaAgenteTemplate">
        <DxLookup v-bind="getLookup('empresa_agente', 'tipo')" />
      </DxColumn>
      <DxColumn data-field="CodigoSubtipo" :caption="$t('operaciones.grid.subtipoEmpresaColaboradora')" cell-template="subtipoEmpresaColaboradoraTemplate">
        <DxLookup v-bind="getLookup('empresa_colaboradora', 'subtipo')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="NombreAgente" :caption="$t('operaciones.grid.agente')"></DxColumn>
      <DxColumn data-field="NombreResponsable" :caption="$t('operaciones.grid.responsable')" />
      <DxColumn data-field="Comercializadora" :caption="$t('operaciones.grid.comercializadora')"></DxColumn>
      <DxColumn data-field="CifTitular" :caption="$t('operaciones.grid.cif_titular')"></DxColumn>
      <DxColumn data-field="DocumentoTitular" :caption="$t('operaciones.grid.dni')"></DxColumn>
      <DxColumn data-field="CodigoEstado" :caption="$t('operaciones.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TieneServiciosAdicionales" :caption="$t('operaciones.grid.tiene_servicios_adicionales')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TarifaAccesoLuz" :caption="$t('operaciones.grid.tarifa_de_acceso_luz')"></DxColumn>
      <DxColumn data-field="CupsLuz" :caption="$t('operaciones.grid.cups_luz')"></DxColumn>
      <DxColumn data-field="TarifaAccesoGas" :caption="$t('operaciones.grid.tarifa_de_acceso_gas')"></DxColumn>
      <DxColumn data-field="CupsGas" :caption="$t('operaciones.grid.cups_gas')"></DxColumn>
      <DxColumn data-field="IdExterno" :caption="$t('operaciones.grid.id_externo')"></DxColumn>
      <DxColumn data-field="Comision" :caption="$t('operaciones.grid.comision')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="Retrocomision" :caption="$t('operaciones.grid.retrocomision')" data-type="number" :calculate-cell-value="formatRetrocomision">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="FechaModificacion" :caption="$t('operaciones.grid.fecha_ultima_modificacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="NombreUsuarioUltimaModificacion" :caption="$t('operaciones.grid.nombre_usuario_ultima_modificacion')" />
      <DxColumn
        type="buttons"
        :caption="$t('operaciones.grid.acciones')"
        :fixed="true"
        fixed-position="right"
        :show-in-column-chooser="false"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('operaciones.grid.ir_a_chat'),
            icon: 'fa-regular fa-comment-dots text-success',
            onClick: (e: any) => irAChat(e),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('operaciones.grid.ver_detalle'),
            icon: 'fa-solid fa-magnifying-glass text-success',
            onClick: (e: any) => verDetalle(e),
          },
        ]"
      />
      <DxColumnFixing :enabled="true" />

      <template #tipoEmpresaAgenteTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2 `, `tipoEmpresaAgente ${data.value.replace(' ', '')}`]">
            {{ $t(`empresa_agente.tipo.${data.value}`) }}
        </div>
      </template>
      <template #estadoTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2 `, `estado-operacion ${data.value.replace(' ', '')}`]">
            {{ $t(`operaciones.estado.${data.value}`) }}
        </div>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #exportButtonTemplate>
        <button @click="onExporting" class="btn btn-sm btn-light d-flex align-items-center gap-2 m-2">
          <i class="fas fa-file-excel exportar-excel"></i>
          <span class="text-primary">{{ $t("shared.ui.buttons.exportToExcel") }}</span>
        </button>
      </template>
      <template #subtipoEmpresaColaboradoraTemplate="{ data }">
        <div v-if="data.value" :class="[`d-inline-block rounded py-1 px-2`, `subtipoEmpresaColaboradora ${data.value.replace(' ', '')}`]">
            {{ $t(`empresa_colaboradora.subtipo.${data.value}`) }}
        </div>
      </template>
      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: operacion }">
        <operaciones-grid-detail :template-data="operacion" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import { DxColumn, DxDataGrid, DxFormat, DxLookup, DxMasterDetail, DxColumnFixing, DxSelection, DxToolbar, DxItem } from "devextreme-vue/data-grid";
import {
  currencyFormat,
  getLookup,
  getColumnLookupBoolean,
  exportGridToExcel,
  getDefaultGridWithStateStoringProperties,
  getColumnChooserBtn,
  getResetFiltersGridBtn,
  getResetStateGridBtn,
} from "@helpers/grid-helper";
import type CustomStore from "devextreme/data/custom_store";
import { onMounted, ref } from "vue";
import OperacionesGridDetail from "@/components/operaciones-energia/OperacionesEnergiaGridDetail.vue";
import type DevExpress from "devextreme";
import { useRefreshGridOnVisible } from "@/.shared/composables/useRefreshGridOnVisible";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { useRouter } from "@/.shared/services/routing-services";
import { TipoOperacionEnum } from "@/.shared/enums/TipoOperacionEnum";

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const emit = defineEmits(["operationSelected", "refreshGridTramites"]);
const router = useRouter();
useRefreshGridOnVisible(grid);

const gridProperties = {
  ...getDefaultGridWithStateStoringProperties("operaciones-energia-grid-state.v7", () => grid.value),
  selection: {
    mode: "single",
  },
  hoverStateEnabled: true,
  rowAlternationEnabled: false,
};

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: await api.toStandardApiFunction("getOperacionesEnergiaForGrid", { showLoading: false }),
  });
});

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  await router.push({ name: "tramite", query: { idOperacion: data.row?.data.Id, tipoOperacion: TipoOperacionEnum.Energia } });
}

async function irAChat(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  const rutaBO = import.meta.env.VITE_BASE_URL;
  const url = `${rutaBO}/comunicaciones-operacion/${data.row?.data.Id}/${data.row?.data.CodigoSecuencial.toString().padStart(10, "0")}/${TipoOperacionEnum.Energia}`;
  window.open(url, "_blank");
}

const formatRetrocomision = (rowData: any) => {
  return rowData.Retrocomision ? -Math.abs(rowData.Retrocomision) : rowData.Retrocomision;
};

function onSelectionChanged(e: DevExpress.ui.dxDataGrid.SelectionChangedEvent) {
  const id = e.selectedRowsData[0]?.Id;
  if (id) {
    emit("operationSelected", id);
  }
}

const onExporting = async () => {
  await exportGridToExcel(grid.value!.instance, "Operaciones.xlsx", "Operaciones", true);
};
</script>

<style lang="scss" scoped>

.lupa,
.chat,
.exportar-excel {
  color: var(--text-success-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}

.grid-operaciones .dx-datagrid {
  max-height: calc(100vh - 250px);
}
</style>
